import {useCallback} from 'react';
import { saveClipCards, updateClipCards } from "../queries/clipQueries";
import { useSelectedCard } from "../contexts/SelectedCardContext";
import { useMaterializationSettings } from "../contexts/MaterializationSettingsContext";
import { useEditorialAssets } from "../contexts/EditorialAssetsContext";
import { useContentSpecificParams } from "../contexts/ContentSpecificParamsContext";
import { PANEL_MODE } from "../types/generic";
import { applyKeyMomentFilter } from "../utils/filterUtils";
import {SUPPORTED_CONTENTS, CURRENT_SPORT, SUPPORTED_SPORTS} from "../constants/globalConstants";
import { EDITORIAL_ASSETS, INTERLEAVE_MODE } from "../types/editorialAssetsTypes";
import { OverlayType } from "../reactvideoeditor/pro/types";
import {L_CUT_OFFSET} from "../constants";
import {CollectionClip} from "../types/collectionTypes";
import {KEY_MOMENT_REPLAY} from "../constants/keyMomentConstants";
import {isReplaySegment, getCurrentLCutOffsetSeconds} from "../utils/editor/editorUtils";
import {trackAction} from "../queries/tracking";
import {TRACKING_CONTENT_TYPE, TRACKING_OUTCOME, UI_TRACKING_ACTION} from "../types/trackingTypes";

export const useClipOperations = () => {
    const { selectedCard, logicClipName, logicClip, setLogicClip, setPanelMode } = useSelectedCard();
    const { fade, fadeDuration, aspectRatio, audioTrack, lCut, savedLCutOffsets } = useMaterializationSettings();
    const { selectedEditorialAssets, assetRenderParametersMap } = useEditorialAssets();
    const { contentSpecificParams } = useContentSpecificParams();

    const getDefaultLCutOffsets = useCallback((cards: any[]): Record<string, number> => {
        return (cards || []).reduce((acc: Record<string, number>, card: any) => {
            const masterCut = card.key_moments?.[0];
            const hasReplay = masterCut?.cuts?.some((cut: any) => (cut.label || '')?.toUpperCase() === 'REPLAY');
            if (hasReplay) {
                acc[String(card.id)] = savedLCutOffsets[String(card.id)] ?? L_CUT_OFFSET;
            }
            return acc;
        }, {});
    }, [savedLCutOffsets]);

    const getProductionSettings = useCallback((contentType: string, timelineOverlays?: any[], cards?: any[]) => {
        const timelineInterleaveOverlayIds = timelineOverlays
            ? new Set(
                timelineOverlays
                    .filter((o: any) => o.type === OverlayType.ASSET && o.subtype === OverlayType.INTERLEAVE)
                    .map((o: any) => String(o.id))
            )
            : null;

        const requestEditorialAssets: Record<string, [{id: string, params: any}]> = {};
        for (const editorialAsset of selectedEditorialAssets) {
            let matchingEntries = Object.entries(assetRenderParametersMap).filter(([key]) => key === editorialAsset.id || key.startsWith(`${editorialAsset.id}_&_`));

            if (
                timelineInterleaveOverlayIds
                && editorialAsset.placement_type === EDITORIAL_ASSETS.INTERLEAVE
                && editorialAsset.parameters?.technical_parameters?.mode !== INTERLEAVE_MODE.EVENT
            ) {
                matchingEntries = matchingEntries.filter(([key]) => {
                    const overlayId = key.split('_&_')[1];
                    return overlayId === undefined || timelineInterleaveOverlayIds.has(overlayId);
                });
            }

            requestEditorialAssets[editorialAsset.placement_type] = (
                matchingEntries.length > 0
                    ? matchingEntries.map(([key, value]) => ({
                        id: editorialAsset.id,
                        params: { data: value?.overlay_params ?? [] }
                    }))
                    : [{ id: editorialAsset.id, params: { data: [] } }]
            ) as [{ id: string; params: any }];
        }
        return {
            fade,
            fade_duration: fadeDuration,
            content_type: contentType,
            aspect_ratio: parseInt(aspectRatio.split(":")[0]) / parseInt(aspectRatio.split(":")[1]),
            lcut: lCut,
            lcut_offset: !lCut
                ? {}
                : timelineOverlays && timelineOverlays.length > 0
                    // Passaggio dall'editor: uso gli offset generati dagli overlay, che riflettono
                    // eventuali personalizzazioni fatte dall'utente sulla timeline.
                    ? timelineOverlays
                        .filter((overlay: any) => isReplaySegment(overlay))
                        .reduce((acc: Record<string, number>, overlay: any) => {
                            acc[String(overlay.idClip)] = getCurrentLCutOffsetSeconds(overlay, timelineOverlays);
                            return acc;
                        }, {})
                    // Nessun passaggio dall'editor: invio comunque un offset di default per ogni
                    // replay tra i key moment selezionati.
                    : getDefaultLCutOffsets(cards || selectedCard),
            ...requestEditorialAssets
        };
    }, [fade, fadeDuration, aspectRatio, selectedEditorialAssets, assetRenderParametersMap, lCut, selectedCard, getDefaultLCutOffsets]);

    const getSuggestSettings = useCallback((contentType: string) => {
        if (contentType === SUPPORTED_CONTENTS[CURRENT_SPORT as keyof typeof SUPPORTED_SPORTS].MATCH_HIGHLIGHT) {
            return {
                min_duration: contentSpecificParams?.minDuration,
                max_duration: contentSpecificParams?.maxDuration
            };
        }
        return {};
    }, [contentSpecificParams]);

    const applyLCut = (cards: CollectionClip[]): CollectionClip[] => {
        const keyMomentCards: CollectionClip[] = JSON.parse(JSON.stringify(cards));

        for(const card of keyMomentCards ){
            if(card.key_moments && card.key_moments.length > 0){
                const masterCut = card.key_moments[0];

                if(masterCut){
                    if(masterCut.cuts.length > 0){
                        let replayIndex = -1;
                        let refIndex = -1;

                        for(let i = 0; i < masterCut.cuts.length; i++){
                            const label = (masterCut.cuts[i].label || '').toUpperCase();
                            if(label === 'REPLAY') replayIndex = i;
                            if(KEY_MOMENT_REPLAY[CURRENT_SPORT].includes(label)) refIndex = i;
                        }

                        if (replayIndex >= 0 && refIndex >= 0){
                            const streamReference = masterCut.stream_reference;
                            const refTrimPoints = masterCut.cuts[refIndex];
                            const replayTrimPoints = masterCut.cuts[replayIndex];

                            const offset = savedLCutOffsets[String(card.id)] ?? L_CUT_OFFSET;

                            if(streamReference !== null && streamReference !== undefined){
                                const replayDuration = replayTrimPoints.end - replayTrimPoints.start;
                                const replayEnd = streamReference + offset + replayDuration;
                                const isOverRef = replayEnd > refTrimPoints.end;

                                if(isOverRef){
                                    // Modifico la clip di riferimento (il goal/azione)
                                    refTrimPoints.end = replayEnd;
                                }
                            }
                        }
                    }
                } else {
                    console.error(`[applyLCut] Errore: No plat cut found for key moment nella card ${card.id}`);
                }
            } else {
                console.error(`[applyLCut] Errore: Nessun key_moments trovato per la card ${card.id}`);
            }
        }
        return keyMomentCards;
    }

    const saveClip = async (contentType: string, overrideCards?: any[], timelineOverlays?: any[]) => {
        const cardsToSave = overrideCards || selectedCard;
        const productionSettings = getProductionSettings(contentType, timelineOverlays, cardsToSave);
        const suggestSettings = getSuggestSettings(contentType);

        try {
            const reqLogicClip = await saveClipCards(
                cardsToSave,
                logicClipName,
                productionSettings,
                suggestSettings,
                audioTrack,
                contentSpecificParams?.keyMomentSettings ? applyKeyMomentFilter(contentSpecificParams.keyMomentSettings) : {items: []}
            );

            trackAction(
                UI_TRACKING_ACTION.CONTENT_CREATION,
                {
                    content_id: reqLogicClip?.id || null,
                    content_type: TRACKING_CONTENT_TYPE.CLIP,
                    clip_content_type: contentType,
                    name: logicClipName,
                    outcome: TRACKING_OUTCOME.SUCCESS
                }
            );

            setLogicClip(reqLogicClip);
            setPanelMode(PANEL_MODE.UPDATE);
            return reqLogicClip;

        } catch (error) {
            trackAction(
                UI_TRACKING_ACTION.CONTENT_CREATION,
                {
                    content_id: null,
                    content_type: TRACKING_CONTENT_TYPE.CLIP,
                    clip_content_type: contentType,
                    name: logicClipName,
                    outcome: TRACKING_OUTCOME.FAILURE
                }
            );

            throw error;
        }
    };


    const overWriteClip = async (contentType: string, overrideCards?: any[], timelineOverlays?: any[]) => {
        const cardsToSave = overrideCards || selectedCard;
        const productionSettings = getProductionSettings(contentType, timelineOverlays, cardsToSave);
        const suggestSettings = getSuggestSettings(contentType);

        trackAction(
            UI_TRACKING_ACTION.CONTENT_EDIT,
            {
                content_id: logicClip?.id || null,
                content_type: TRACKING_CONTENT_TYPE.CLIP,
                clip_content_type: contentType,
                name: logicClipName,
            }
        )

        const reqLogicClip = await updateClipCards(
            logicClip?.id,
            cardsToSave,
            logicClipName,
            productionSettings,
            suggestSettings,
            audioTrack,
            contentSpecificParams?.keyMomentSettings ? applyKeyMomentFilter(contentSpecificParams.keyMomentSettings) : {items: []}
        );

        setLogicClip(reqLogicClip);
        return reqLogicClip;
    };

    return { saveClip, overWriteClip, applyLCut };
};